import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  Timestamp,
  onSnapshot,
  DocumentData,
  QueryDocumentSnapshot,
  runTransaction,
} from 'firebase/firestore';
import {
  Project,
  ProjectMember,
  AdultSponsor,
  ProjectStatus,
} from '../types/project.types';
import { TeacherClass } from '../types/teacher.types';

const convertProject = (doc: QueryDocumentSnapshot<DocumentData>): Project => {
  const data = doc.data();
  return {
    id: doc.id,
    fairType: data.fairType,
    name: data.name,
    projectCode: data.projectCode,
    status: data.status,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
    adultSponsor: data.adultSponsor,
    memberIds: data.memberIds,
    members: data.members.map((member: any) => ({
      ...member,
      joinedAt: member.joinedAt.toDate(),
    })),
    maxTeamSize: data.maxTeamSize,
    classId: data.classId,
    categories: data.categories || [],
    fairId: data.fairId,
    judgingStatus: data.judgingStatus || 'not_started',
  };
};

export async function getAllProjects(): Promise<Project[]> {
  const snapshot = await getDocs(collection(db, 'projects'));
  return snapshot.docs.map((doc) => convertProject(doc));
}

export async function getProject(projectId: string): Promise<Project | null> {
  const projectRef = doc(db, 'projects', projectId);
  const projectDoc = await getDoc(projectRef);
  if (!projectDoc.exists()) return null;
  return convertProject(projectDoc);
}

export const projectsService = {
  getProject: getProject,
  async canCreateProject(studentId: string): Promise<boolean> {
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('memberIds', 'array-contains', studentId),
      where('status', 'not-in', ['archived', 'pending_purge'])
    );
    const snapshot = await getDocs(q);
    return snapshot.empty;
  },

  async createProject(
    name: string,
    fairId: string,
    creator: ProjectMember,
    adultSponsor: AdultSponsor,
    classId?: string,
    categories: {
      id: string;
      name: string;
      description: string;
      isHighSchool: boolean;
      isElementary: boolean;
    }[] = []
  ): Promise<Project> {
    const generateCode = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      return Array.from(Array(5))
        .map(() => chars.charAt(Math.floor(Math.random() * chars.length)))
        .join('');
    };

    const isCodeUnique = async (code: string) => {
      const q = query(
        collection(db, 'projects'),
        where('projectCode', '==', code)
      );
      const snapshot = await getDocs(q);
      return snapshot.empty;
    };

    let projectCode;
    do {
      projectCode = generateCode();
    } while (!(await isCodeUnique(projectCode)));

    const timestamp = new Date();
    const projectData = {
      name,
      fairId,
      fairType: 'highSchool' as const,
      projectCode,
      status: 'draft' as ProjectStatus,
      createdAt: Timestamp.fromDate(timestamp),
      updatedAt: Timestamp.fromDate(timestamp),
      adultSponsor,
      categories: categories.map((cat) => cat.id),
      memberIds: [creator.userId],
      members: [
        {
          ...creator,
          joinedAt: Timestamp.fromDate(timestamp),
        },
      ],
      maxTeamSize: 3,
      ...(classId && { classId }),
    };

    const docRef = await addDoc(collection(db, 'projects'), projectData);
    const project = {
      id: docRef.id,
      ...projectData,
      createdAt: timestamp,
      updatedAt: timestamp,
      judgingStatus: 'not_started',
      members: [
        {
          ...creator,
          joinedAt: timestamp,
        },
      ],
    };

    if (classId) {
      await projectsService.updateClassWithProjectAndMember(
        classId,
        project,
        creator
      );
    }

    return project;
  },

  async updateClassWithProjectAndMember(
    classId: string,
    project: Project,
    member: ProjectMember
  ): Promise<void> {
    const classDocRef = doc(db, 'classes', classId);

    try {
      await runTransaction(db, async (transaction) => {
        const classDoc = await transaction.get(classDocRef);

        if (!classDoc.exists()) {
          throw new Error('Class not found');
        }

        const classData = classDoc.data() as TeacherClass;

        const projectExists = classData.projects?.some(
          (p) => p.projectId === project.id
        );
        const updatedProjects = projectExists
          ? classData.projects
          : [
              ...(classData.projects || []),
              {
                projectId: project.id,
                projectName: project.name,
                students: [member.userId],
                createdAt: new Date(),
              },
            ];

        const studentExists = classData.students?.some(
          (s) => s.userId === member.userId
        );
        const updatedStudents = studentExists
          ? classData.students
          : [
              ...(classData.students || []),
              {
                userId: member.userId,
                firstName: member.firstName,
                lastName: member.lastName,
                email: member.email,
              },
            ];

        transaction.update(classDocRef, {
          projects: updatedProjects,
          students: updatedStudents,
          updatedAt: Timestamp.fromDate(new Date()),
        });
      });
    } catch (error) {
      console.error('Error updating class document:', error);
      throw error;
    }
  },

  async getStudentProjects(studentId: string): Promise<Project[]> {
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('memberIds', 'array-contains', studentId),
      where('status', 'not-in', ['archived', 'pending_purge'])
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return [];
    return snapshot.docs.map((doc) => convertProject(doc));
  },

  async getStudentProject(studentId: string): Promise<Project | null> {
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('memberIds', 'array-contains', studentId),
      where('status', 'not-in', ['archived', 'pending_purge'])
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return convertProject(snapshot.docs[0]);
  },

  async joinProject(projectCode: string, member: ProjectMember): Promise<void> {
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('projectCode', '==', projectCode),
      where('status', 'not-in', ['archived', 'pending_purge'])
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error('Project not found');
    }

    const projectDoc = snapshot.docs[0];
    const project = convertProject(projectDoc);

    if (project.members.length >= project.maxTeamSize) {
      throw new Error('Project team is full');
    }

    if (project.members.some((m) => m.userId === member.userId)) {
      throw new Error('Already a member of this project');
    }

    const timestamp = new Date();
    await updateDoc(projectDoc.ref, {
      memberIds: [...project.memberIds, member.userId],
      members: [
        ...project.members,
        {
          ...member,
          joinedAt: Timestamp.fromDate(timestamp),
        },
      ],
      updatedAt: Timestamp.fromDate(timestamp),
    });

    if (project.classId) {
      await this.updateClassWithProjectAndMember(
        project.classId,
        {
          ...project,
          members: [
            ...project.members,
            {
              ...member,
              joinedAt: timestamp,
            },
          ],
        },
        member
      );
    }
  },

  subscribeToProjects(callback: (projects: Project[]) => void): () => void {
    const projectsRef = collection(db, 'projects');
    const q = query(projectsRef, where('status', 'not-in', ['pending_purge']));

    return onSnapshot(q, (snapshot) => {
      const projects = snapshot.docs.map(convertProject);
      callback(projects);
    });
  },

  // Subscribe to all projects in a region
  subscribeToProjectsInRegion(
    regionId: string,
    callback: (projects: Project[]) => void
  ): () => void {
    const projectsRef = collection(db, 'projects');
    const q = query(projectsRef, where('regionId', '==', regionId));

    return onSnapshot(
      q,
      (snapshot) => {
        const projects = snapshot.docs
          .map(convertProject)
          .filter((project) => project.status !== 'pending_purge');

        callback(projects);
      },
      (error) => {
        console.error('Error in projects subscription:', error);
      }
    );
  },

  subscribeToProjectsInRegionByFairs(
    regionId: string,
    callback: (projects: Project[]) => void
  ): () => void {
    // First, set up a listener for fairs in the region
    const fairsRef = collection(db, 'fairs');
    const fairsQuery = query(fairsRef, where('regionId', '==', regionId));

    // We'll track the project subscription to clean it up when needed
    let projectsUnsubscribe: (() => void) | null = null;

    // Subscribe to fairs
    const fairsUnsubscribe = onSnapshot(
      fairsQuery,
      (fairsSnapshot) => {
        // Get all fair IDs in this region
        const fairIds = fairsSnapshot.docs.map((doc) => doc.id);

        // If no fairs found, return empty array
        if (fairIds.length === 0) {
          callback([]);
          return;
        }

        // Clean up any existing project subscription
        if (projectsUnsubscribe) {
          projectsUnsubscribe();
        }

        // Now query for projects with these fair IDs
        const projectsRef = collection(db, 'projects');
        const projectsQuery = query(
          projectsRef,
          where('fairId', 'in', fairIds)
        );

        // Subscribe to projects
        projectsUnsubscribe = onSnapshot(
          projectsQuery,
          (projectsSnapshot) => {
            const projects = projectsSnapshot.docs
              .map(convertProject)
              .filter((project) => project.status !== 'pending_purge');

            callback(projects);
          },
          (error) => {
            console.error('Error in projects subscription:', error);
          }
        );
      },
      (error) => {
        console.error('Error in fairs subscription:', error);
      }
    );
    // Return a function that cleans up both subscriptions
    return () => {
      fairsUnsubscribe();
      if (projectsUnsubscribe) {
        projectsUnsubscribe();
      }
    };
  },

  // Subscribe to a student's project
  subscribeToStudentProjects(
    uid: string,
    callback: (projects: Project[]) => void
  ): () => void {
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('memberIds', 'array-contains', uid),
      where('status', 'not-in', ['archived', 'pending_purge'])
    );
    return onSnapshot(q, (snapshot) => {
      const projects: Project[] = snapshot.docs.map(convertProject);
      callback(projects);
    });
  },

  async removeMember(projectId: string, userId: string): Promise<void> {
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }

    const project = convertProject(projectDoc);
    const updatedMembers = project.members.filter((m) => m.userId !== userId);
    const updatedMemberIds = project.memberIds.filter((id) => id !== userId);

    if (
      project.members.find((m) => m.userId === userId)?.role === 'creator' &&
      updatedMembers.length > 0
    ) {
      updatedMembers[0].role = 'creator';
    }

    const timestamp = new Date();
    if (updatedMembers.length === 0) {
      await updateDoc(projectRef, {
        status: 'pending_purge',
        updatedAt: Timestamp.fromDate(timestamp),
      });
    } else {
      await updateDoc(projectRef, {
        members: updatedMembers,
        memberIds: updatedMemberIds,
        updatedAt: Timestamp.fromDate(timestamp),
      });
    }
  },

  async updateProjectStatus(
    projectId: string,
    status: ProjectStatus,
    updatedBy: string,
    comments?: string
  ): Promise<void> {
    const projectRef = doc(db, 'projects', projectId);
    const timestamp = new Date();
    await updateDoc(projectRef, {
      status,
      updatedAt: Timestamp.fromDate(timestamp),
      lastReviewedBy: updatedBy,
      lastReviewedAt: Timestamp.fromDate(timestamp),
      ...(comments && { statusComments: comments }),
    });
  },

  async areAllFormsReviewed(projectId: string): Promise<boolean> {
    const formsRef = collection(db, 'forms');
    const q = query(
      formsRef,
      where('projectContext.projectId', '==', projectId)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return false;
    return snapshot.docs.every((doc) => {
      const status = doc.data().status;
      return status === 'approved' || status === 'rejected';
    });
  },
};
